import { Classes, Position } from '@blueprintjs/core';
// eslint-disable-next-line import/no-named-as-default, import/no-named-as-default-member
import storeLoader from 'app/stores/storeLoader';
import classNames from 'classnames';
import { Box, Button, Flex, Link, Menu, Popover, Suspense, Text } from 'core/components';
import { inject } from 'mobx-react';
import React, { Component } from 'react';
import {
  DEFAULT_FLOW_MONITORING_TEMPLATE_PRESET_ID,
  DEFAULT_MONITORING_TEMPLATE_PRESET_ID,
  ESSENTIALS_FEATURE_FLAG,
  getEffectiveDefaultPresetId
} from '@kentik/ui-shared/nms/deviceMonitoringTemplates';
import MonitoringTemplateForm from './MonitoringTemplateForm';

@storeLoader('$metrics.monitoringTemplatesCollection')
@inject('$metrics', '$auth')
export default class MonitoringTemplateSelector extends Component {
  static defaultProps = {
    label: 'Monitoring Template',
    minimal: false,
    inlineLabel: false,
    placeholder: 'Select…',
    withDefault: true,
    templateType: 'NMS' // Default to NMS type
  };

  componentDidUpdate(prevProps) {
    const { loading, model, value, withDefault, $metrics, $auth, templateType, form } = this.props;

    // storeLoader finished
    if (prevProps.loading && !loading) {
      // When rendered inside a <Field>, model isn't passed directly;
      // the form's model is available via the form prop instead.
      const deviceModel = model || form?.model;
      const id = value || deviceModel?.get('monitoring_template_id');
      const { defaultFlowTemplate } = $metrics.monitoringTemplatesCollection;

      // Only auto-assign a default template for genuinely new devices.
      // Existing devices without a template should remain unassigned
      // rather than silently picking up the current default.
      const isNewDevice = deviceModel?.isNew;
      const shouldAutoAssignNms = isNewDevice && templateType === 'NMS' && withDefault && !Number(id);
      const shouldAutoAssignFlow = isNewDevice && templateType === 'FLOW' && withDefault && !Number(id);

      // pick the correct default based on the feature flag:
      // essentials when the flag is on, everything when it's off.
      if (shouldAutoAssignNms) {
        const hasEssentialsPermission = $auth.hasPermission(ESSENTIALS_FEATURE_FLAG, { overrideForSudo: false });
        const defaultPresetId = getEffectiveDefaultPresetId(hasEssentialsPermission);
        const nmsDefault = this.filteredPresets.find((preset) => preset.get('preset') === defaultPresetId);
        if (nmsDefault) {
          this.handleChange(nmsDefault.id);
        }
      }

      if (shouldAutoAssignFlow && defaultFlowTemplate) {
        this.handleChange(defaultFlowTemplate.id);
      }
    }
  }

  state = {
    newTemplateModel: null,
    isOpen: false
  };

  get selectedTemplate() {
    const { value, model, $metrics } = this.props;
    const collection = $metrics.monitoringTemplatesCollection;
    const id = value || model?.get('monitoring_template_id');
    return id ? collection.get(id) : null;
  }

  get filteredTemplates() {
    const { $metrics, $auth, templateType } = this.props;
    const collection = $metrics.monitoringTemplatesCollection;
    const hasEssentialsPermission = $auth.hasPermission(ESSENTIALS_FEATURE_FLAG, { overrideForSudo: false });

    // Filter templates by type
    // hide Essentials preset when feature flag is off
    return collection.models.filter((template) => {
      const matchesType = template.get('template_type') === templateType || template.get('template_type') == null;
      if (!matchesType) {
        return false;
      }

      // Hide Essentials preset when the flag is off
      if (!hasEssentialsPermission && template.get('preset') === DEFAULT_MONITORING_TEMPLATE_PRESET_ID) {
        return false;
      }

      return true;
    });
  }

  get filteredPresets() {
    return this.filteredTemplates.filter((model) => model.isPreset);
  }

  get filteredCustomTemplates() {
    return this.filteredTemplates.filter((model) => !model.isPreset);
  }

  handleChange = (value) => {
    const { onChange, model } = this.props;

    if (onChange) {
      onChange(value);
    } else if (model) {
      model.set('monitoring_template_id', value);
    }
  };

  handleOpen = (isOpen = true) => {
    this.setState({ isOpen });
  };

  handleCreate = (sourceTemplate = null, isFlowTemplate = false) => {
    const { $metrics, $auth, templateType } = this.props;
    const collection = $metrics.monitoringTemplatesCollection;
    const hasEssentialsPermission = $auth.hasPermission(ESSENTIALS_FEATURE_FLAG, { overrideForSudo: false });
    const defaultTemplateId = isFlowTemplate
      ? DEFAULT_FLOW_MONITORING_TEMPLATE_PRESET_ID
      : getEffectiveDefaultPresetId(hasEssentialsPermission);
    const defaultTemplate = this.filteredTemplates.find((t) => t.get('preset') === defaultTemplateId);
    const model = sourceTemplate
      ? sourceTemplate.duplicate()
      : (defaultTemplate || collection.defaultTemplate).duplicate({
          isNew: true,
          template_type: templateType
        });

    this.setState({ newTemplateModel: model });
  };

  handleCloseTemplateDialog = () => {
    this.setState(({ newTemplateModel }) => {
      if (newTemplateModel?.isNew === false) {
        this.handleChange(newTemplateModel.id);
      }
      return { newTemplateModel: null };
    });
  };

  optionRenderer = (template, selected) => (
    <li key={template.id}>
      <Button
        className={classNames(Classes.MENU_ITEM, Classes.POPOVER_DISMISS, {
          [Classes.ACTIVE]: template.id === selected?.id
        })}
        textAlign="left"
        onClick={() => (template.id === selected?.id ? null : this.handleChange(template.id))}
        minimal
        ellipsis
      >
        <Box>{template.get('name')}</Box>
        <Text muted small>
          {template.get('description')}
        </Text>
      </Button>
    </li>
  );

  get buttonLabel() {
    const { showButtonLabel, label, loading, placeholder } = this.props;
    const { selectedTemplate } = this;
    if (showButtonLabel && label) {
      return label;
    }
    if (loading) {
      return 'Loading…';
    }
    if (!selectedTemplate) {
      return placeholder;
    }
    if (selectedTemplate) {
      return selectedTemplate.get('name');
    }
    return label;
  }

  render() {
    const { fill, inlineLabel, loading, minimal, popoverProps, templateType } = this.props;
    const { newTemplateModel, isOpen } = this.state;
    const { selectedTemplate } = this;
    const isCreatingTemplateModel = newTemplateModel !== null;

    const templateTypeLabel = templateType === 'FLOW' ? 'Flow' : 'NMS';

    return (
      <>
        <Popover
          canEscapeKeyClose
          modifiers={{
            flip: { enabled: true, boundariesElement: 'viewport' },
            preventOverflow: { enabled: true, boundariesElement: 'viewport' },
            offset: { enabled: true, offset: '0,2px' }
          }}
          content={
            <Menu width={315} overflow="hidden" borderRadius={3}>
              <Suspense loading={loading}>
                <Box maxHeight={350} overflow="auto">
                  <li className={Classes.MENU_HEADER}>
                    <h6 className={Classes.HEADING}>Presets ({templateTypeLabel})</h6>
                  </li>
                  {this.filteredPresets.map((template) => this.optionRenderer(template, selectedTemplate))}
                  {this.filteredCustomTemplates.length > 0 && (
                    <>
                      <li className={Classes.MENU_HEADER}>
                        <h6 className={Classes.HEADING}>Your templates ({templateTypeLabel})</h6>
                      </li>
                      {this.filteredCustomTemplates.map((template) => this.optionRenderer(template, selectedTemplate))}
                    </>
                  )}
                </Box>
                <li style={{ padding: '7px' }}>
                  <Button
                    onClick={() => this.handleCreate(null, templateType === 'FLOW')}
                    className={classNames(Classes.MENU_ITEM, Classes.POPOVER_DISMISS)}
                    icon="add"
                    rightIcon="blank"
                    text={`Create ${templateTypeLabel} Template`}
                    fill
                  />
                  <Flex mt="6px" justifyContent="center">
                    <Link to="/v4/settings/monitoring-templates" blank>
                      Manage templates
                    </Link>
                  </Flex>
                </li>
              </Suspense>
            </Menu>
          }
          isOpen={isOpen}
          onClose={() => this.handleOpen(false)}
          position={Position.BOTTOM_LEFT}
          minimal
          {...popoverProps}
        >
          <Button
            active={isOpen}
            onClick={() => this.handleOpen(!isOpen)}
            rightIcon="caret-down"
            textAlign="left"
            height={inlineLabel ? 38 : undefined}
            minWidth={fill ? undefined : 145}
            fill={fill}
            minimal={minimal}
          >
            {inlineLabel && (
              <Text as="div" muted small pb="1px">
                {templateTypeLabel}
              </Text>
            )}
            <Text
              as="div"
              fontWeight={inlineLabel ? 'bold' : 'normal'}
              muted={loading || !selectedTemplate}
              small={inlineLabel}
            >
              {this.buttonLabel}
            </Text>
          </Button>
        </Popover>
        {isCreatingTemplateModel && (
          <MonitoringTemplateForm
            model={newTemplateModel}
            isDialog
            isOpen
            onClose={this.handleCloseTemplateDialog}
            templateType={templateType}
          />
        )}
      </>
    );
  }
}
